import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown, Settings, HelpCircle, LogOut, UserCircle } from "lucide-react";
import type { User } from "@supabase/supabase-js";

export interface UserAvatarProps {
  user: User | null;
  size?: "sm" | "md" | "lg";
  showBadge?: boolean;
  variant?: "compact" | "full" | "sidebar";
  menuPlacement?: "bottom" | "top";
  onLogout: () => void;
}

function getInitials(user: User | null): string {
  if (!user) return "?";
  const meta = user.user_metadata as { full_name?: string } | undefined;
  const fullName = meta?.full_name?.trim();
  if (fullName) {
    const parts = fullName.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase().slice(0, 2);
    }
    return fullName.slice(0, 2).toUpperCase();
  }
  const email = user.email?.split("@")[0] || "";
  return (email.slice(0, 2) || "U").toUpperCase();
}

const MENU_ITEMS = [
  { id: "compte" as const, label: "Gérer mon compte", icon: UserCircle, path: "/compte" },
  { id: "parametres" as const, label: "Paramètres", icon: Settings, path: "/parametres" },
  { id: "aide" as const, label: "Aide", icon: HelpCircle, path: "/aide" },
];

export default function UserAvatar({
  user,
  size = "md",
  showBadge = true,
  variant = "compact",
  menuPlacement = variant === "sidebar" ? "top" : "bottom",
  onLogout,
}: UserAvatarProps) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const initials = getInitials(user);
  const displayName = user?.user_metadata?.full_name || user?.email?.split("@")[0] || "Utilisateur";

  const sizeMap = { sm: 36, md: 44, lg: 52 };
  const avatarSize = sizeMap[size];

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleItemClick = (path: string) => {
    navigate(path);
    setOpen(false);
  };

  const isSidebar = variant === "sidebar";

  return (
    <div ref={containerRef} style={{ position: "relative" }}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        aria-haspopup="true"
        style={{
          display: "flex",
          alignItems: "center",
          gap: variant === "full" || isSidebar ? 12 : 0,
          padding: variant === "full" ? "8px 12px" : isSidebar ? "10px 12px" : 0,
          width: variant === "compact" ? avatarSize : "100%",
          border: "none",
          background:
            variant === "full" ? "white" : isSidebar ? "rgba(255,255,255,0.45)" : "transparent",
          borderRadius: variant === "full" || isSidebar ? 12 : "50%",
          cursor: "pointer",
          fontFamily: "'IBM Plex Sans', system-ui, sans-serif",
          borderWidth: variant === "compact" ? 2 : 1,
          borderStyle: "solid",
          borderColor: variant === "compact"
            ? "rgba(255,255,255,0.4)"
            : isSidebar
              ? "rgba(154, 52, 18, 0.18)"
              : "#EAE0D5",
          boxShadow: "none",
          transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
        }}
        onMouseOver={(e) => {
          e.currentTarget.style.transform = "scale(1.02)";
        }}
        onMouseOut={(e) => {
          e.currentTarget.style.transform = "scale(1)";
        }}
      >
        <div
          style={{
            position: "relative",
            width: avatarSize,
            height: avatarSize,
            borderRadius: "50%",
            background: isSidebar
              ? "linear-gradient(145deg, #FDBA74 0%, #EA580C 100%)"
              : "linear-gradient(135deg, #FF9500 0%, #FF6B35 100%)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <span
            style={{
              fontSize: size === "sm" ? "0.75rem" : size === "md" ? "0.9rem" : "1rem",
              fontWeight: 800,
              color: "white",
              textTransform: "uppercase",
              letterSpacing: "0.02em",
              userSelect: "none",
            }}
          >
            {initials}
          </span>
          {showBadge && (
            <span
              title="En ligne"
              style={{
                position: "absolute",
                bottom: 2,
                right: 2,
                width: 10,
                height: 10,
                background: "#22C55E",
                borderRadius: "50%",
                border: "2px solid white",
                boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
              }}
            />
          )}
        </div>
        {(variant === "full" || isSidebar) && (
          <>
            {isSidebar && (
              <div style={{ flex: 1, overflow: "hidden", textAlign: "left", minWidth: 0 }}>
                <div
                  style={{
                    fontSize: "0.8rem",
                    fontWeight: 700,
                    color: "#9A3412",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {displayName}
                </div>
                <div
                  style={{
                    fontSize: "0.7rem",
                    color: "#B45309",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {user?.email || "—"}
                </div>
              </div>
            )}
            <ChevronDown
              size={18}
              strokeWidth={2}
              style={{
                color: isSidebar ? "#C2410C" : "#9A8878",
                transition: "transform 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
                transform: open ? "rotate(180deg)" : "rotate(0deg)",
                flexShrink: 0,
                marginLeft: isSidebar ? "auto" : 0,
              }}
            />
          </>
        )}
      </button>

      {open && (
        <>
          <div
            style={{ position: "fixed", inset: 0, zIndex: 49 }}
            onClick={() => setOpen(false)}
            aria-hidden="true"
          />
          <div
            style={{
              position: "absolute",
              ...(menuPlacement === "top"
                ? { bottom: "100%", marginBottom: 8 }
                : { top: "100%", marginTop: 8 }),
              right: 0,
              background: "white",
              border: "1px solid #EAE0D5",
              borderRadius: 12,
              boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
              padding: "6px 0",
              minWidth: 200,
              zIndex: 50,
              animation: "userAvatarSlideIn 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
              overflow: "hidden",
            }}
          >
            {MENU_ITEMS.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => handleItemClick(item.path)}
                style={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  padding: "12px 16px",
                  border: "none",
                  background: "none",
                  color: "#5A4A3A",
                  fontSize: "0.9rem",
                  fontWeight: 500,
                  cursor: "pointer",
                  textAlign: "left",
                  fontFamily: "'IBM Plex Sans', system-ui, sans-serif",
                  transition: "background 0.15s",
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.background = "#f8fafc";
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.background = "none";
                }}
              >
                <item.icon size={18} strokeWidth={1.8} style={{ color: "#9A8878", flexShrink: 0 }} />
                {item.label}
              </button>
            ))}
            <div style={{ height: 1, background: "#EAE0D5", margin: "4px 0" }} />
            <button
              type="button"
              onClick={() => {
                onLogout();
                setOpen(false);
              }}
              style={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                gap: 12,
                padding: "12px 16px",
                border: "none",
                background: "none",
                color: "#EF4444",
                fontSize: "0.9rem",
                fontWeight: 600,
                cursor: "pointer",
                textAlign: "left",
                fontFamily: "'IBM Plex Sans', system-ui, sans-serif",
                transition: "background 0.15s",
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.background = "#fef2f2";
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.background = "none";
              }}
            >
              <LogOut size={18} strokeWidth={1.8} style={{ flexShrink: 0 }} />
              Déconnexion
            </button>
          </div>
        </>
      )}

      <style>{`
        @keyframes userAvatarSlideIn {
          from {
            opacity: 0;
            transform: translateY(-8px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
}
